import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Handshake, Plus, FilePdf, Sparkle, CheckSquare, Square, FloppyDisk, Trash,
  ArrowRight, MagnifyingGlass, CopySimple, ArrowsClockwise, X, Receipt,
  ListChecks, Files, Coins, Buildings, ShieldCheck,
} from '@phosphor-icons/react';
import ReportToolbar from '../../components/ReportToolbar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLOR = {
  draft: 'bg-slate-100 text-slate-700 border-slate-300',
  pending: 'bg-amber-50 text-amber-700 border-amber-300',
  active: 'bg-emerald-50 text-emerald-700 border-emerald-300',
  renewed: 'bg-blue-50 text-blue-700 border-blue-300',
  expired: 'bg-orange-50 text-orange-700 border-orange-300',
  terminated: 'bg-red-50 text-red-700 border-red-300',
};

const LAYOUTS = [
  { v: 'classic', label: 'Classic' },
  { v: 'modern_band', label: 'Modern Band' },
  { v: 'minimalist', label: 'Minimalist' },
];

function getToken() {
  return localStorage.getItem('erp_token')
    || localStorage.getItem('access_token')
    || localStorage.getItem('token');
}

// ====== Customer dropdown (reused pattern from JobOrdersPage) ======
function CustomerPicker({ customers, value, onPick }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const selected = customers.find(c => c.id === value);
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return customers.slice(0, 25);
    return customers.filter(c =>
      (c.name || '').toLowerCase().includes(t)
      || (c.company_name || '').toLowerCase().includes(t)
      || (c.phone || '').toLowerCase().includes(t)
    ).slice(0, 25);
  }, [customers, q]);
  return (
    <div className="relative" data-testid="agr-cust-picker">
      <Input
        placeholder="Search customer…"
        value={open ? q : (selected?.name || '')}
        onFocus={() => { setOpen(true); setQ(''); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onChange={e => setQ(e.target.value)}
        data-testid="agr-cust-search"
      />
      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-popover border border-border rounded-md shadow-lg max-h-72 overflow-auto">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-xs text-muted-foreground">No customer found.</div>
          ) : filtered.map(c => (
            <button key={c.id} type="button"
                    className="w-full text-left px-3 py-2 text-xs hover:bg-muted/60 border-b border-border/40 last:border-0"
                    onMouseDown={() => { onPick(c); setOpen(false); }}>
              <div className="font-medium">{c.name}</div>
              {(c.company_name || c.phone) && <div className="text-muted-foreground text-[10px] truncate">{[c.company_name, c.phone].filter(Boolean).join(' · ')}</div>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ====== Simple list editor for client_scope / exclusions / inclusion lists ======
function StringListEditor({ value = [], onChange, placeholder, testid }) {
  const add = () => onChange([...(value || []), '']);
  const update = (i, v) => onChange(value.map((x, idx) => idx === i ? v : x));
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-1" data-testid={testid}>
      {(value || []).map((v, i) => (
        <div key={i} className="flex gap-1">
          <Input value={v} onChange={e => update(i, e.target.value)} placeholder={placeholder} className="h-7 text-xs" />
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600" onClick={() => remove(i)}><Trash size={12} /></Button>
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={add} className="h-6 text-xs"><Plus size={10} className="mr-1" /> Add</Button>
    </div>
  );
}

// ====================================================================
export default function AgreementsPage() {
  const [meta, setMeta] = useState(null);
  const [agreements, setAgreements] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterTrade, setFilterTrade] = useState('all');
  const [search, setSearch] = useState('');
  const [showExpiringOnly, setShowExpiringOnly] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [working, setWorking] = useState(false);
  const [aiBusy, setAiBusy] = useState({});
  const [activeTab, setActiveTab] = useState('details');

  const blank = {
    agreement_type: 'amc_comprehensive',
    trades: [],
    title: '',
    layout_template: 'classic',
    customer_id: '', customer_name: '', customer_address: '', customer_trn: '',
    customer_contact_person: '', customer_phone: '', customer_email: '',
    site_locations: [], site_address: '',
    start_date: new Date().toISOString().slice(0, 10),
    end_date: new Date(Date.now() + 365 * 24 * 3600 * 1000).toISOString().slice(0, 10),
    auto_renew: true,
    renewal_notice_days: 30,
    escalation_percent: 5,
    annual_value: 0, total_value: 0, currency: 'AED', payment_schedule: 'quarterly',
    payment_terms: [],
    equipment_list: [],
    visit_frequency: 'Monthly',
    visits_per_year: 12,
    emergency_response_time: '4 hours',
    delivery_schedule: '',
    spare_parts_included: false,
    spare_parts_inclusion_list: [],
    spare_parts_exclusion_list: [],
    preamble: '',
    scope_of_work: '',
    boq: [],
    checklist: [],
    client_scope: [],
    exclusions: [],
    terms_and_conditions: '',
    termination_clause: '',
    special_conditions: '',
    contractor_signatory: { role: 'contractor', name: '', position: '', company: '', signed_at: '' },
    client_signatory: { role: 'client', name: '', position: '', company: '', signed_at: '' },
    seed_from_template: true,
  };
  const [form, setForm] = useState(blank);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (showExpiringOnly) params.set('expiring_within_days', '60');
      if (filterStatus !== 'all') params.set('status', filterStatus);
      if (filterType !== 'all') params.set('agreement_type', filterType);
      if (filterTrade !== 'all') params.set('trade', filterTrade);
      if (search.trim()) params.set('q', search.trim());
      const [mRes, aRes, cRes] = await Promise.all([
        axios.get(`${API}/agreements/meta`),
        axios.get(`${API}/agreements/?${params.toString()}`),
        axios.get(`${API}/contacts?contact_type=customer`).catch(() => ({ data: [] })),
      ]);
      setMeta(mRes.data);
      setAgreements(aRes.data || []);
      setCustomers(cRes.data || []);
    } catch (e) { toast.error('Failed to load agreements'); }
    finally { setLoading(false); }
  }, [filterStatus, filterType, filterTrade, search, showExpiringOnly]);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openNew = () => { setEditing(null); setForm({ ...blank }); setActiveTab('details'); setFormOpen(true); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({ ...blank, ...a, contractor_signatory: a.contractor_signatory || blank.contractor_signatory, client_signatory: a.client_signatory || blank.client_signatory });
    setActiveTab('details');
    setFormOpen(true);
  };

  const save = async () => {
    setWorking(true);
    try {
      let saved;
      if (editing) {
        // Strip seed_from_template — only allowed on create
        const { seed_from_template, ...patch } = form;
        const res = await axios.patch(`${API}/agreements/${editing.id}`, patch);
        saved = res.data;
        toast.success('Agreement updated');
      } else {
        const res = await axios.post(`${API}/agreements/`, form);
        saved = res.data;
        toast.success(`Created ${saved.agreement_number}`);
      }
      setEditing(saved);
      setForm(f => ({ ...f, ...saved }));
      fetchAll();
      return saved;
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setWorking(false); }
  };

  const saveAndPdf = async () => { const s = await save(); if (s) downloadPdf(s); };

  const action = async (a, what, extraQuery = '') => {
    try {
      await axios.post(`${API}/agreements/${a.id}/${what}${extraQuery}`);
      toast.success(`${a.agreement_number}: ${what}`);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || `${what} failed`); }
  };

  const duplicateAgr = async (a) => {
    try {
      const r = await axios.post(`${API}/agreements/${a.id}/duplicate`);
      toast.success(`Duplicated → ${r.data.agreement_number}`);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Duplicate failed'); }
  };

  const renewAgr = async (a) => {
    try {
      const r = await axios.post(`${API}/agreements/${a.id}/renew`);
      toast.success(`Renewed → ${r.data.agreement_number} (Draft)`);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Renew failed'); }
  };

  const downloadPdf = (a) => {
    fetch(`${API}/agreements/${a.id}/pdf`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => r.blob())
      .then(blob => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `agreement-${a.agreement_number}.pdf`;
        link.click();
        URL.revokeObjectURL(link.href);
      })
      .catch(() => toast.error('PDF download failed'));
  };

  const generateInvoice = async (pt) => {
    if (!editing) return;
    try {
      const r = await axios.post(`${API}/agreements/${editing.id}/payment-terms/${pt.id}/generate-invoice`);
      toast.success(`Invoice ${r.data.invoice_number} created (${editing.currency} ${r.data.amount.toFixed(2)})`);
      // Reload to reflect invoice_generated=true
      const refreshed = await axios.get(`${API}/agreements/${editing.id}`);
      setEditing(refreshed.data); setForm(f => ({ ...f, ...refreshed.data }));
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not generate invoice'); }
  };

  // AI helpers
  const callAi = async (kind, fieldKey) => {
    setAiBusy(b => ({ ...b, [kind]: true }));
    try {
      const res = await axios.post(`${API}/agreements/ai/${kind}`, {
        agreement_type: form.agreement_type, trades: form.trades, equipment_list: form.equipment_list,
        site_address: form.site_address, customer_name: form.customer_name,
        visit_frequency: form.visit_frequency, emergency_response_time: form.emergency_response_time,
        payment_schedule: form.payment_schedule, spare_parts_included: form.spare_parts_included,
        start_date: form.start_date, end_date: form.end_date,
      });
      if (kind === 'checklist') {
        setForm(f => ({ ...f, checklist: [...f.checklist, ...res.data.checklist] }));
        toast.success(`AI added ${res.data.checklist.length} checklist items`);
      } else if (kind === 'exclusions') {
        const items = res.data.exclusions || [];
        setForm(f => ({ ...f, exclusions: items }));
        toast.success(`AI added ${items.length} exclusions`);
      } else {
        setForm(f => ({ ...f, [fieldKey]: res.data[fieldKey] }));
        toast.success('AI draft inserted');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'AI failed'); }
    finally { setAiBusy(b => ({ ...b, [kind]: false })); }
  };

  // Equipment / trades toggles
  const toggleTrade = (t) => setForm(f => ({ ...f, trades: f.trades.includes(t) ? f.trades.filter(x => x !== t) : [...f.trades, t] }));
  const toggleEquipment = (eq) => setForm(f => ({ ...f, equipment_list: f.equipment_list.includes(eq) ? f.equipment_list.filter(x => x !== eq) : [...f.equipment_list, eq] }));

  // Apply template — re-seed checklist from selected trades
  const seedFromTemplates = () => {
    if (!meta) return;
    const items = [];
    form.trades.forEach(t => {
      const block = meta.trade_templates[t] || {};
      (block.major || []).forEach(d => items.push({ id: crypto.randomUUID?.() || String(Math.random()), category: 'major', trade: t, description: d, frequency: 'Annual' }));
      (block.minor || []).forEach(d => items.push({ id: crypto.randomUUID?.() || String(Math.random()), category: 'minor', trade: t, description: d, frequency: 'Monthly' }));
    });
    setForm(f => ({ ...f, checklist: [...f.checklist, ...items] }));
    toast.success(`Seeded ${items.length} template items`);
  };

  // Checklist editor
  const addChecklistRow = () => setForm(f => ({ ...f, checklist: [...f.checklist, { id: crypto.randomUUID?.() || String(Math.random()), category: 'major', trade: form.trades[0] || 'HVAC', description: '', frequency: 'Annual' }] }));
  const updateChecklist = (i, k, v) => setForm(f => ({ ...f, checklist: f.checklist.map((c, idx) => idx === i ? { ...c, [k]: v } : c) }));
  const removeChecklist = (i) => setForm(f => ({ ...f, checklist: f.checklist.filter((_, idx) => idx !== i) }));

  // BOQ editor
  const addBoqRow = () => setForm(f => ({ ...f, boq: [...f.boq, { id: crypto.randomUUID?.() || String(Math.random()), description: '', quantity: 1, unit: 'Nos', unit_price: 0 }] }));
  const updateBoq = (i, k, v) => setForm(f => ({ ...f, boq: f.boq.map((b, idx) => idx === i ? { ...b, [k]: v } : b) }));
  const removeBoq = (i) => setForm(f => ({ ...f, boq: f.boq.filter((_, idx) => idx !== i) }));

  // Payment terms editor (also auto-built on save)
  const addPt = () => setForm(f => ({ ...f, payment_terms: [...f.payment_terms, { id: crypto.randomUUID?.() || String(Math.random()), label: '', due_date: '', amount: 0, percent: 0, invoice_generated: false }] }));
  const updatePt = (i, k, v) => setForm(f => ({ ...f, payment_terms: f.payment_terms.map((p, idx) => idx === i ? { ...p, [k]: v } : p) }));
  const removePt = (i) => setForm(f => ({ ...f, payment_terms: f.payment_terms.filter((_, idx) => idx !== i) }));

  // Sites multi
  const addSite = () => setForm(f => ({ ...f, site_locations: [...(f.site_locations || []), ''] }));
  const updateSite = (i, v) => setForm(f => ({ ...f, site_locations: f.site_locations.map((s, idx) => idx === i ? v : s) }));
  const removeSite = (i) => setForm(f => ({ ...f, site_locations: f.site_locations.filter((_, idx) => idx !== i) }));

  // Pick customer
  const pickCustomer = (c) => setForm(f => ({
    ...f,
    customer_id: c.id, customer_name: c.name, customer_address: c.address || f.customer_address,
    customer_trn: c.tax_id || f.customer_trn, customer_phone: c.phone || f.customer_phone, customer_email: c.email || f.customer_email,
  }));

  const editable = !editing || ['draft', 'pending'].includes(editing.status);

  return (
    <div className="container mx-auto p-4 lg:p-6" data-testid="agreements-page">
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2"><Handshake size={28} weight="duotone" /> Agreements</h1>
          <p className="text-muted-foreground text-sm">AMC, Service Agreements, One-time Contracts &amp; SLAs. AI-drafted with branded PDF.</p>
        </div>
        <Button onClick={openNew} data-testid="new-agreement-btn"><Plus size={16} className="mr-2" /> New Agreement</Button>
      </motion.div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search by AGR#, client, site, title…" value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9" data-testid="agr-search" />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-36 h-9" data-testid="agr-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {['draft', 'pending', 'active', 'renewed', 'expired', 'terminated'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-48 h-9" data-testid="agr-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {(meta?.types || []).map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterTrade} onValueChange={setFilterTrade}>
          <SelectTrigger className="w-32 h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All trades</SelectItem>
            {(meta?.trades || []).map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="inline-flex items-center gap-1 text-xs ml-2">
          <input type="checkbox" checked={showExpiringOnly} onChange={e => setShowExpiringOnly(e.target.checked)} className="accent-amber-500" />
          Expiring (60d)
        </label>
        <ReportToolbar testidPrefix="agr-toolbar" downloadBaseName="agreements" exportUrl={null} />
      </div>

      <Card className="p-0 overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-muted-foreground">Loading…</div>
        ) : agreements.length === 0 ? (
          <div className="py-16 text-center text-muted-foreground">
            <Handshake size={48} weight="duotone" className="mx-auto mb-3 opacity-40" />
            <p>No agreements match the current filter.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold uppercase text-xs">AGR #</th>
                  <th className="text-left px-3 py-2 font-semibold uppercase text-xs">Title / Client</th>
                  <th className="text-left px-3 py-2 font-semibold uppercase text-xs">Type</th>
                  <th className="text-left px-3 py-2 font-semibold uppercase text-xs">Trades</th>
                  <th className="text-left px-3 py-2 font-semibold uppercase text-xs">Period</th>
                  <th className="text-right px-3 py-2 font-semibold uppercase text-xs">Value</th>
                  <th className="text-left px-3 py-2 font-semibold uppercase text-xs">Status</th>
                  <th className="text-right px-3 py-2 font-semibold uppercase text-xs">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {agreements.map(a => (
                  <tr key={a.id} className="hover:bg-muted/30" data-testid={`agr-row-${a.id}`}>
                    <td className="px-3 py-2 font-mono font-semibold">{a.agreement_number}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{a.title || a.customer_name}</div>
                      <div className="text-xs text-muted-foreground">{a.customer_name}</div>
                    </td>
                    <td className="px-3 py-2 text-xs">{(meta?.types || []).find(t => t.value === a.agreement_type)?.label?.split(' — ')[0] || a.agreement_type}</td>
                    <td className="px-3 py-2 text-xs">{(a.trades || []).join(', ')}</td>
                    <td className="px-3 py-2 text-xs whitespace-nowrap">{a.start_date} → {a.end_date}</td>
                    <td className="px-3 py-2 text-xs text-right font-mono">{a.currency || 'AED'} {(a.total_value || 0).toLocaleString()}</td>
                    <td className="px-3 py-2">
                      <Badge variant="outline" className={STATUS_COLOR[a.status] || ''}>{a.status}</Badge>
                    </td>
                    <td className="px-3 py-2 text-right space-x-1 whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(a)} data-testid={`open-agr-${a.id}`} title="Open / Edit">Open</Button>
                      <Button size="sm" variant="ghost" onClick={() => downloadPdf(a)} title="PDF" data-testid={`pdf-agr-${a.id}`}><FilePdf size={14} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => duplicateAgr(a)} title="Duplicate" data-testid={`dup-agr-${a.id}`}><CopySimple size={14} /></Button>
                      {a.status === 'draft' && (
                        <Button size="sm" variant="default" onClick={() => action(a, 'submit')} title="Submit" data-testid={`submit-agr-${a.id}`}>Submit</Button>
                      )}
                      {(a.status === 'draft' || a.status === 'pending') && (
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => action(a, 'activate')} title="Activate" data-testid={`activate-agr-${a.id}`}><ArrowRight size={12} /></Button>
                      )}
                      {['active', 'expired', 'terminated'].includes(a.status) && (
                        <Button size="sm" variant="outline" onClick={() => renewAgr(a)} title="Renew" data-testid={`renew-agr-${a.id}`}><ArrowsClockwise size={14} /></Button>
                      )}
                      {['active', 'renewed', 'pending'].includes(a.status) && (
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => action(a, 'terminate', '?reason=Terminated%20by%20user')} title="Terminate" data-testid={`terminate-agr-${a.id}`}><X size={14} /></Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* =============== FORM =============== */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="agr-form">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Handshake size={20} weight="duotone" />
              {editing ? `Agreement ${editing.agreement_number}` : 'New Agreement'}
              {editing && <Badge variant="outline" className={STATUS_COLOR[editing.status] || ''}>{editing.status}</Badge>}
              {!editable && <Badge variant="outline" className="text-[10px]">Read-only fields after activation — only signatories / payment-terms / status can change</Badge>}
            </DialogTitle>
            <DialogDescription>
              Pick a type and trades, fill client / period / commercials. AI can draft Preamble, Scope, Checklist, T&amp;C, Termination. Generate branded PDF in 3 layouts.
            </DialogDescription>
          </DialogHeader>

          <div className="flex gap-1 border-b border-border mb-2 mt-1 flex-shrink-0 overflow-x-auto">
            {[
              { v: 'details', label: '1. Details', icon: Buildings },
              { v: 'scope', label: '2. Scope & Equipment', icon: ListChecks },
              { v: 'checklist', label: `3. Checklists (${form.checklist.length})`, icon: CheckSquare },
              { v: 'boq', label: `4. BOQ (${form.boq.length})`, icon: Files },
              { v: 'commercials', label: '5. Commercials & Payment', icon: Coins },
              { v: 'terms', label: '6. T&C / Termination', icon: ShieldCheck },
              { v: 'signoff', label: '7. Sign-off', icon: ShieldCheck },
            ].map(t => (
              <button key={t.v} onClick={() => setActiveTab(t.v)}
                      className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px whitespace-nowrap transition ${activeTab === t.v ? 'border-blue-600 text-blue-700' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                      data-testid={`agr-tab-${t.v}`}>
                <t.icon size={12} className="inline mr-1" /> {t.label}
              </button>
            ))}
          </div>

          <ScrollArea className="flex-1 pr-3">
            {activeTab === 'details' && (
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                  <div className="lg:col-span-2">
                    <Label>Title</Label>
                    <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. AMC for Marina Tower HVAC" data-testid="agr-title" />
                  </div>
                  <div>
                    <Label>Type</Label>
                    <Select value={form.agreement_type} onValueChange={v => setForm({ ...form, agreement_type: v })}>
                      <SelectTrigger data-testid="agr-type-input"><SelectValue /></SelectTrigger>
                      <SelectContent>{(meta?.types || []).map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="lg:col-span-3">
                    <Label>Trades <span className="text-xs text-muted-foreground">(multi-select drives the checklist seeds)</span></Label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {(meta?.trades || []).map(t => (
                        <button key={t} type="button" onClick={() => toggleTrade(t)}
                                className={`px-3 py-1 rounded-full text-xs border transition ${form.trades.includes(t) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white dark:bg-slate-800 hover:bg-blue-50 dark:hover:bg-slate-700'}`}
                                data-testid={`agr-trade-${t}`}>
                          {form.trades.includes(t) ? '✓ ' : ''}{t}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="lg:col-span-2">
                    <Label>Client <span className="text-red-500">*</span></Label>
                    <CustomerPicker customers={customers} value={form.customer_id} onPick={pickCustomer} />
                  </div>
                  <div>
                    <Label>Customer TRN</Label>
                    <Input value={form.customer_trn} onChange={e => setForm({ ...form, customer_trn: e.target.value })} />
                  </div>
                  <div className="lg:col-span-3">
                    <Label>Customer address</Label>
                    <Textarea rows={2} value={form.customer_address} onChange={e => setForm({ ...form, customer_address: e.target.value })} />
                  </div>
                  <div>
                    <Label>Contact person</Label>
                    <Input value={form.customer_contact_person} onChange={e => setForm({ ...form, customer_contact_person: e.target.value })} />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input value={form.customer_phone} onChange={e => setForm({ ...form, customer_phone: e.target.value })} />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input value={form.customer_email} onChange={e => setForm({ ...form, customer_email: e.target.value })} />
                  </div>
                  <div className="lg:col-span-3">
                    <Label>Primary site address</Label>
                    <Textarea rows={2} value={form.site_address} onChange={e => setForm({ ...form, site_address: e.target.value })} placeholder="Primary site / property address" />
                  </div>
                  <div className="lg:col-span-3">
                    <Label>Additional sites (multi-site agreements)</Label>
                    <StringListEditor value={form.site_locations || []} onChange={v => setForm({ ...form, site_locations: v })} placeholder="Tower B, 2nd Floor" testid="agr-sites" />
                  </div>

                  <div>
                    <Label>Start date</Label>
                    <Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} data-testid="agr-start" />
                  </div>
                  <div>
                    <Label>End date</Label>
                    <Input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} data-testid="agr-end" />
                  </div>
                  <div>
                    <Label className="flex items-center gap-2">Auto-renew
                      <input type="checkbox" checked={!!form.auto_renew} onChange={e => setForm({ ...form, auto_renew: e.target.checked })} className="ml-2" />
                    </Label>
                    <div className="flex gap-1 items-center mt-1 text-xs">
                      <span>Notice (days):</span>
                      <Input type="number" value={form.renewal_notice_days} onChange={e => setForm({ ...form, renewal_notice_days: parseInt(e.target.value) || 0 })} className="h-7 w-16" />
                      <span className="ml-2">Escalation %:</span>
                      <Input type="number" step="0.1" value={form.escalation_percent} onChange={e => setForm({ ...form, escalation_percent: parseFloat(e.target.value) || 0 })} className="h-7 w-16" />
                    </div>
                  </div>
                  <div>
                    <Label>PDF Layout</Label>
                    <Select value={form.layout_template} onValueChange={v => setForm({ ...form, layout_template: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{LAYOUTS.map(l => <SelectItem key={l.v} value={l.v}>{l.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  {!editing && (
                    <div className="lg:col-span-3">
                      <label className="flex items-center gap-2 text-xs">
                        <input type="checkbox" checked={form.seed_from_template} onChange={e => setForm({ ...form, seed_from_template: e.target.checked })} />
                        <strong>Start with template checklists</strong>
                        <span className="text-muted-foreground">— pre-seeds Major + Minor checklists per selected trade. Untick to start blank and let AI generate.</span>
                      </label>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'scope' && (
              <div className="space-y-3 py-2">
                <div className="border border-blue-200 bg-blue-50/40 rounded-md p-3 text-xs">
                  Define Preamble + Scope of Work + Equipment. Use AI to draft based on the agreement type, trades and equipment list.
                </div>
                <div>
                  <Label className="flex items-center justify-between">
                    Equipment list
                    <span className="text-xs text-muted-foreground">multi-select for AHU/FCU/Chiller/Pumps/etc.</span>
                  </Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {['AHU', 'FAHU', 'FCU', 'Pumps – Chilled Water', 'Pumps – Booster', 'Electrical DB', 'Generator', 'Water Heater', 'Cooling Tower', 'VRV/VRF', 'Chiller', 'Solar Panels', 'BMS Controller', 'Fire Pump'].map(eq => (
                      <button key={eq} type="button" onClick={() => toggleEquipment(eq)}
                              className={`px-3 py-1 rounded-full text-xs border transition ${form.equipment_list.includes(eq) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white dark:bg-slate-800 hover:bg-blue-50 dark:hover:bg-slate-700'}`}
                              data-testid={`agr-eq-${eq}`}>
                        {form.equipment_list.includes(eq) ? '✓ ' : ''}{eq}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label>Visit frequency</Label>
                    <Input value={form.visit_frequency} onChange={e => setForm({ ...form, visit_frequency: e.target.value })} placeholder="Monthly / Quarterly / Bi-monthly" />
                  </div>
                  <div>
                    <Label>Visits / year</Label>
                    <Input type="number" value={form.visits_per_year || 0} onChange={e => setForm({ ...form, visits_per_year: parseInt(e.target.value) || 0 })} />
                  </div>
                  <div>
                    <Label>Emergency response time</Label>
                    <Input value={form.emergency_response_time} onChange={e => setForm({ ...form, emergency_response_time: e.target.value })} placeholder="2 hours / 4 hours / 24 hours" />
                  </div>
                  <div className="col-span-3">
                    <Label>Delivery schedule</Label>
                    <Input value={form.delivery_schedule} onChange={e => setForm({ ...form, delivery_schedule: e.target.value })} placeholder="e.g. PPM every 30 days; breakdowns within 4h" />
                  </div>
                </div>

                <div className="border rounded-md p-3 bg-blue-50/30 dark:bg-blue-900/10">
                  <div className="flex items-center justify-between mb-1">
                    <Label className="m-0">Preamble</Label>
                    <Button size="sm" variant="outline" onClick={() => callAi('preamble', 'preamble')} disabled={aiBusy.preamble} data-testid="agr-ai-preamble">
                      <Sparkle size={12} className="mr-1" /> {aiBusy.preamble ? 'Drafting…' : 'AI draft'}
                    </Button>
                  </div>
                  <Textarea rows={6} value={form.preamble} onChange={e => setForm({ ...form, preamble: e.target.value })} placeholder="Auto-drafted preamble will appear here…" />
                </div>

                <div className="border rounded-md p-3 bg-blue-50/30 dark:bg-blue-900/10">
                  <div className="flex items-center justify-between mb-1">
                    <Label className="m-0">Scope of Work</Label>
                    <Button size="sm" variant="outline" onClick={() => callAi('scope', 'scope_of_work')} disabled={aiBusy.scope} data-testid="agr-ai-scope">
                      <Sparkle size={12} className="mr-1" /> {aiBusy.scope ? 'Drafting…' : 'AI draft'}
                    </Button>
                  </div>
                  <Textarea rows={10} value={form.scope_of_work} onChange={e => setForm({ ...form, scope_of_work: e.target.value })} placeholder="Editable on drafts. Use AI to seed, then refine." className="font-mono text-xs" />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Client scope / responsibilities</Label>
                    <StringListEditor value={form.client_scope || []} onChange={v => setForm({ ...form, client_scope: v })} placeholder="e.g. Provide site access, electricity, water…" testid="agr-client-scope" />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label>Exclusions</Label>
                      <Button size="sm" variant="outline" onClick={() => callAi('exclusions')} disabled={aiBusy.exclusions} data-testid="agr-ai-exclusions">
                        <Sparkle size={12} className="mr-1" /> {aiBusy.exclusions ? 'Drafting…' : 'AI suggest'}
                      </Button>
                    </div>
                    <StringListEditor value={form.exclusions || []} onChange={v => setForm({ ...form, exclusions: v })} placeholder="e.g. Civil Defence re-inspection fees, Insurance premiums…" testid="agr-exclusions" />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div className="flex items-center gap-2">
                    <label className="text-xs"><input type="checkbox" checked={form.spare_parts_included} onChange={e => setForm({ ...form, spare_parts_included: e.target.checked })} className="mr-1" /> Spare parts INCLUDED in contract value</label>
                  </div>
                  <div>
                    <Label className="text-xs">Spares — included list</Label>
                    <StringListEditor value={form.spare_parts_inclusion_list || []} onChange={v => setForm({ ...form, spare_parts_inclusion_list: v })} placeholder="Filters, belts, fuses…" />
                  </div>
                  <div>
                    <Label className="text-xs">Spares — excluded list</Label>
                    <StringListEditor value={form.spare_parts_exclusion_list || []} onChange={v => setForm({ ...form, spare_parts_exclusion_list: v })} placeholder="Compressors, motors > 5kW…" />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'checklist' && (
              <div className="space-y-3 py-2">
                <div className="border border-blue-200 bg-blue-50/40 rounded-md p-3 text-xs">
                  Major and Minor service checklists for the agreement. Pre-seeded from your selected trades on creation. You can add manually, use AI to extend, or paste your own.
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={addChecklistRow} data-testid="agr-add-checklist-row"><Plus size={12} className="mr-1" /> Add row</Button>
                  <Button size="sm" variant="outline" onClick={seedFromTemplates} disabled={!form.trades.length} title="Append default Major/Minor items from your selected trades">+ Append template items</Button>
                  <Button size="sm" variant="outline" onClick={() => callAi('checklist')} disabled={aiBusy.checklist || !form.trades.length} data-testid="agr-ai-checklist">
                    <Sparkle size={12} className="mr-1" /> {aiBusy.checklist ? 'Generating…' : 'AI generate'}
                  </Button>
                  {form.checklist.length > 0 && (
                    <Button size="sm" variant="ghost" className="text-red-600 ml-auto" onClick={() => setForm(f => ({ ...f, checklist: [] }))}>Clear all</Button>
                  )}
                </div>
                {form.checklist.length === 0 ? (
                  <div className="border border-dashed rounded-md p-8 text-center text-sm text-muted-foreground">No items. Pick trades on tab 1 then click "+ Append template items" or "AI generate".</div>
                ) : (
                  <table className="w-full text-xs border border-border rounded-md">
                    <thead className="bg-muted/30">
                      <tr>
                        <th className="text-left px-2 py-1 w-24">Trade</th>
                        <th className="text-left px-2 py-1 w-24">Category</th>
                        <th className="text-left px-2 py-1">Description</th>
                        <th className="text-left px-2 py-1 w-28">Frequency</th>
                        <th className="w-8"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {form.checklist.map((c, i) => (
                        <tr key={c.id || i}>
                          <td className="px-2 py-1">
                            <select value={c.trade || ''} onChange={e => updateChecklist(i, 'trade', e.target.value)} className="h-7 text-xs border rounded w-full bg-background">
                              <option value="">—</option>
                              {(meta?.trades || []).map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                          </td>
                          <td className="px-2 py-1">
                            <select value={c.category || 'major'} onChange={e => updateChecklist(i, 'category', e.target.value)} className="h-7 text-xs border rounded w-full bg-background">
                              <option value="major">Major</option>
                              <option value="minor">Minor</option>
                              <option value="one_time">One-time</option>
                            </select>
                          </td>
                          <td className="px-2 py-1"><Input value={c.description || ''} onChange={e => updateChecklist(i, 'description', e.target.value)} className="h-7 text-xs" /></td>
                          <td className="px-2 py-1"><Input value={c.frequency || ''} onChange={e => updateChecklist(i, 'frequency', e.target.value)} className="h-7 text-xs" placeholder="Annual / Monthly" /></td>
                          <td><Button size="sm" variant="ghost" className="text-red-600 h-7 w-7 p-0" onClick={() => removeChecklist(i)}><Trash size={12} /></Button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {activeTab === 'boq' && (
              <div className="space-y-3 py-2">
                <div className="border border-blue-200 bg-blue-50/40 rounded-md p-3 text-xs">
                  Bill of Quantities — equipment, parts, labour line items if relevant for the contract.
                </div>
                <Button size="sm" variant="outline" onClick={addBoqRow} data-testid="agr-add-boq-row"><Plus size={12} className="mr-1" /> Add row</Button>
                {form.boq.length === 0 ? (
                  <div className="border border-dashed rounded-md p-8 text-center text-sm text-muted-foreground">No BOQ lines.</div>
                ) : (
                  <table className="w-full text-xs border border-border rounded-md">
                    <thead className="bg-muted/30">
                      <tr>
                        <th className="text-left px-2 py-1">Description</th>
                        <th className="text-center w-20">Qty</th>
                        <th className="text-left w-24">Unit</th>
                        <th className="text-right w-24">Rate</th>
                        <th className="text-right w-28">Amount</th>
                        <th className="w-8"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {form.boq.map((b, i) => (
                        <tr key={b.id || i}>
                          <td className="px-2 py-1"><Input value={b.description || ''} onChange={e => updateBoq(i, 'description', e.target.value)} className="h-7 text-xs" /></td>
                          <td className="px-2 py-1"><Input type="number" value={b.quantity || 0} onChange={e => updateBoq(i, 'quantity', parseFloat(e.target.value) || 0)} className="h-7 text-xs text-center" /></td>
                          <td className="px-2 py-1"><Input value={b.unit || ''} onChange={e => updateBoq(i, 'unit', e.target.value)} className="h-7 text-xs" /></td>
                          <td className="px-2 py-1"><Input type="number" value={b.unit_price || 0} onChange={e => updateBoq(i, 'unit_price', parseFloat(e.target.value) || 0)} className="h-7 text-xs text-right" /></td>
                          <td className="px-2 py-1 text-right font-mono">{((b.quantity || 0) * (b.unit_price || 0)).toLocaleString()}</td>
                          <td><Button size="sm" variant="ghost" className="text-red-600 h-7 w-7 p-0" onClick={() => removeBoq(i)}><Trash size={12} /></Button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {activeTab === 'commercials' && (
              <div className="space-y-3 py-2">
                <div className="grid grid-cols-4 gap-3">
                  <div>
                    <Label>Annual value</Label>
                    <Input type="number" value={form.annual_value || 0} onChange={e => setForm({ ...form, annual_value: parseFloat(e.target.value) || 0 })} data-testid="agr-annual-value" />
                  </div>
                  <div>
                    <Label>Total value</Label>
                    <Input type="number" value={form.total_value || 0} onChange={e => setForm({ ...form, total_value: parseFloat(e.target.value) || 0 })} data-testid="agr-total-value" />
                  </div>
                  <div>
                    <Label>Currency</Label>
                    <Input value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })} />
                  </div>
                  <div>
                    <Label>Payment schedule</Label>
                    <Select value={form.payment_schedule} onValueChange={v => setForm({ ...form, payment_schedule: v })}>
                      <SelectTrigger data-testid="agr-payment-schedule"><SelectValue /></SelectTrigger>
                      <SelectContent>{(meta?.payment_schedules || []).map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Label className="m-0">Payment terms / instalments</Label>
                  <Button size="sm" variant="outline" onClick={addPt} className="ml-2"><Plus size={12} className="mr-1" /> Add</Button>
                  <span className="text-xs text-muted-foreground ml-2">Auto-built on save from schedule + period + total value.</span>
                </div>
                {form.payment_terms.length === 0 ? (
                  <div className="border border-dashed rounded-md p-6 text-center text-xs text-muted-foreground">No payment terms — will be auto-generated on first save.</div>
                ) : (
                  <table className="w-full text-xs border border-border rounded-md">
                    <thead className="bg-muted/30">
                      <tr>
                        <th className="text-left px-2 py-1">Label</th>
                        <th className="text-left px-2 py-1 w-32">Due date</th>
                        <th className="text-right px-2 py-1 w-20">%</th>
                        <th className="text-right px-2 py-1 w-28">Amount</th>
                        <th className="text-left px-2 py-1 w-40">Invoice</th>
                        <th className="w-8"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {form.payment_terms.map((p, i) => (
                        <tr key={p.id || i}>
                          <td className="px-2 py-1"><Input value={p.label || ''} onChange={e => updatePt(i, 'label', e.target.value)} className="h-7 text-xs" /></td>
                          <td className="px-2 py-1"><Input type="date" value={p.due_date || ''} onChange={e => updatePt(i, 'due_date', e.target.value)} className="h-7 text-xs" /></td>
                          <td className="px-2 py-1"><Input type="number" step="0.1" value={p.percent || 0} onChange={e => updatePt(i, 'percent', parseFloat(e.target.value) || 0)} className="h-7 text-xs text-right" /></td>
                          <td className="px-2 py-1"><Input type="number" value={p.amount || 0} onChange={e => updatePt(i, 'amount', parseFloat(e.target.value) || 0)} className="h-7 text-xs text-right" /></td>
                          <td className="px-2 py-1">
                            {p.invoice_generated ? (
                              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-300 text-[10px]">
                                <Receipt size={10} className="mr-1" /> {p.invoice_number}
                              </Badge>
                            ) : editing && ['active', 'renewed'].includes(editing.status) ? (
                              <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => generateInvoice(p)} data-testid={`gen-inv-${p.id}`}>
                                <Receipt size={10} className="mr-1" /> Generate invoice
                              </Button>
                            ) : <span className="text-muted-foreground text-[10px]">—</span>}
                          </td>
                          <td><Button size="sm" variant="ghost" className="text-red-600 h-7 w-7 p-0" onClick={() => removePt(i)}><Trash size={12} /></Button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {activeTab === 'terms' && (
              <div className="space-y-3 py-2">
                <div className="border rounded-md p-3 bg-blue-50/30">
                  <div className="flex items-center justify-between mb-1">
                    <Label className="m-0">Terms and Conditions</Label>
                    <Button size="sm" variant="outline" onClick={() => callAi('terms', 'terms_and_conditions')} disabled={aiBusy.terms} data-testid="agr-ai-terms">
                      <Sparkle size={12} className="mr-1" /> {aiBusy.terms ? 'Drafting…' : 'AI draft'}
                    </Button>
                  </div>
                  <Textarea rows={14} value={form.terms_and_conditions} onChange={e => setForm({ ...form, terms_and_conditions: e.target.value })} placeholder="Editable on drafts. Use AI draft to seed UAE-style T&Cs." className="font-mono text-xs" />
                </div>
                <div className="border rounded-md p-3 bg-amber-50/30">
                  <div className="flex items-center justify-between mb-1">
                    <Label className="m-0">Termination Clause</Label>
                    <Button size="sm" variant="outline" onClick={() => callAi('termination', 'termination_clause')} disabled={aiBusy.termination} data-testid="agr-ai-termination">
                      <Sparkle size={12} className="mr-1" /> {aiBusy.termination ? 'Drafting…' : 'AI draft'}
                    </Button>
                  </div>
                  <Textarea rows={8} value={form.termination_clause} onChange={e => setForm({ ...form, termination_clause: e.target.value })} placeholder="Convenience + breach + insolvency + effects of termination" className="font-mono text-xs" />
                </div>
                <div>
                  <Label>Special Conditions</Label>
                  <Textarea rows={5} value={form.special_conditions} onChange={e => setForm({ ...form, special_conditions: e.target.value })} placeholder="Any one-off conditions specific to this contract" />
                </div>
              </div>
            )}

            {activeTab === 'signoff' && (
              <div className="space-y-4 py-2">
                <div className="border border-blue-200 bg-blue-50/40 rounded-md p-3 text-xs">
                  Signatory details for both parties — printed on the agreement PDF.
                </div>
                {[
                  { key: 'contractor_signatory', label: 'Contractor signatory (Your company)' },
                  { key: 'client_signatory', label: 'Client signatory' },
                ].map(blk => {
                  const sig = form[blk.key] || {};
                  const set = (k, v) => setForm(f => ({ ...f, [blk.key]: { ...(f[blk.key] || {}), [k]: v } }));
                  return (
                    <div key={blk.key} className="border rounded-md p-3">
                      <Label className="mb-2 block">{blk.label}</Label>
                      <div className="grid grid-cols-2 gap-3">
                        <div><Label className="text-xs">Name</Label><Input value={sig.name || ''} onChange={e => set('name', e.target.value)} data-testid={`${blk.key}-name`} /></div>
                        <div><Label className="text-xs">Position / Designation</Label><Input value={sig.position || ''} onChange={e => set('position', e.target.value)} placeholder="General Manager / Facilities Manager" data-testid={`${blk.key}-position`} /></div>
                        <div><Label className="text-xs">For &amp; on behalf of (Company)</Label><Input value={sig.company || ''} onChange={e => set('company', e.target.value)} /></div>
                        <div><Label className="text-xs">Signed on</Label><Input type="date" value={sig.signed_at || ''} onChange={e => set('signed_at', e.target.value)} /></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>

          <DialogFooter className="border-t pt-3 flex-shrink-0 flex-wrap gap-2">
            <Button variant="outline" onClick={() => setFormOpen(false)}>Close</Button>
            {editing && (
              <>
                <Button variant="outline" onClick={() => downloadPdf(editing)} data-testid="agr-pdf-download">
                  <FilePdf size={14} className="mr-1" /> Download PDF
                </Button>
                <Button variant="outline" onClick={() => duplicateAgr(editing)} data-testid="agr-duplicate">
                  <CopySimple size={14} className="mr-1" /> Duplicate
                </Button>
              </>
            )}
            <Button variant="outline" onClick={saveAndPdf} disabled={working} data-testid="agr-save-pdf">
              <FilePdf size={14} className="mr-1" /> Save &amp; Download PDF
            </Button>
            <Button onClick={save} disabled={working} data-testid="agr-save">
              <FloppyDisk size={14} className="mr-1" /> {editing ? 'Update' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
